import { createSlice } from "@reduxjs/toolkit";
import { UPLOAD_ENTITY_TYPES } from "constants/documentUploads";

const MISC_ANALYSIS_BASE = "/nextgen/document-editors/misc-analysis";
const MISC_ANALYSIS_PROPOSAL_WRITING = `${MISC_ANALYSIS_BASE}/proposal-writing`;

const OUTLINE_ORDERED_LIST_PARAMS = { sortBy: "OUTLINE" };

export const DOCUMENT_EDITOR_CONFIG = {
  [UPLOAD_ENTITY_TYPES.CONTRACT]: {
    endpoint: "/nextgen/document-editors/contract",
    paramKey: "contractId",
    // No addinUploadEndpoint: Contract isn't a selectable entity type in the add-in
    // wizard today, so it has no /addin-upload counterpart yet.
    addinUploadRevisionEndpoint:
      "/nextgen/document-editors/contract/addin-upload-revision",
    getEditorUrl: (contractId) => {
      return `/contracts/all/${contractId}/summary/documents`;
    },
  },
  [UPLOAD_ENTITY_TYPES.PROPOSAL]: {
    endpoint: "/nextgen/document-editors/proposal",
    listParams: OUTLINE_ORDERED_LIST_PARAMS,
    paramKey: "proposalId",
    addinUploadEndpoint: "/nextgen/document-editors/proposal/addin-upload",
    addinUploadRevisionEndpoint:
      "/nextgen/document-editors/proposal/addin-upload-revision",
    getEditorUrl: (proposalId, documentId, isArchived = false) =>
      `/proposals/${isArchived ? "archived" : "view"}/${proposalId}/writing/view/${documentId}`,
  },
  [UPLOAD_ENTITY_TYPES.OPPORTUNITY]: {
    endpoint: "/nextgen/document-editors/opportunity",
    paramKey: "opportunityId",
    addinUploadEndpoint: "/nextgen/document-editors/opportunity/addin-upload",
    addinUploadRevisionEndpoint:
      "/nextgen/document-editors/opportunity/addin-upload-revision",
    getEditorUrl: (opportunityId, documentId, isArchived) =>
      `/capture/${isArchived ? "archived-opportunities" : "tracked-opportunities"}/${opportunityId}/research/view/${documentId}`,
  },
  [UPLOAD_ENTITY_TYPES.MISC_ANALYSIS_RESEARCH]: {
    endpoint: MISC_ANALYSIS_BASE,
    listEndpoint: `${MISC_ANALYSIS_BASE}/research`,
    paramKey: "miscAnalysisId",
    addinUploadEndpoint: `${MISC_ANALYSIS_BASE}/addin-upload`,
    addinUploadRevisionEndpoint: `${MISC_ANALYSIS_BASE}/addin-upload-revision`,
    getEditorUrl: (miscAnalysisId, documentId, isArchived = false) =>
      `/misc-analysis/${isArchived ? "archived" : "view"}/${miscAnalysisId}/writing/view/${documentId}`,
  },
  [UPLOAD_ENTITY_TYPES.MISC_ANALYSIS_PROPOSAL_WRITING]: {
    endpoint: MISC_ANALYSIS_BASE,
    listEndpoint: MISC_ANALYSIS_PROPOSAL_WRITING,
    listParams: OUTLINE_ORDERED_LIST_PARAMS,
    createEndpoint: MISC_ANALYSIS_PROPOSAL_WRITING,
    uploadEndpoint: MISC_ANALYSIS_PROPOSAL_WRITING,
    // Not nested under /proposal-writing on the add-in side — the backend exposes this
    // as a distinct flat verb under the misc-analysis base, not a sibling of the
    // regular (non-addin) uploadEndpoint above.
    addinUploadEndpoint: `${MISC_ANALYSIS_BASE}/addin-proposal-writing-upload`,
    // Revision is a single endpoint regardless of research/proposal-writing subtype —
    // it operates by documentId directly, same as MISC_ANALYSIS_RESEARCH's.
    addinUploadRevisionEndpoint: `${MISC_ANALYSIS_BASE}/addin-upload-revision`,
    paramKey: "miscAnalysisId",
    getEditorUrl: (miscAnalysisId, documentId, isArchived = false) =>
      `/misc-analysis/${isArchived ? "archived" : "view"}/${miscAnalysisId}/proposal-writing/view/${documentId}`,
  },
  [UPLOAD_ENTITY_TYPES.MISC_ANALYSIS]: {
    endpoint: MISC_ANALYSIS_BASE,
    paramKey: "miscAnalysisId",
  },
  UNLINKED: {
    endpoint: "/nextgen/document-editors/unlinked",
    paramKey: "unlinkedEntityId",
  },
};

const createInitialEntityState = () => ({
  documents: [],
  isLoading: false,
  isReadabilityView: false,
  readabilityScores: {},
  readabilityThresholds: null,
});

export const INITIAL_STATE = Object.fromEntries(
  Object.keys(DOCUMENT_EDITOR_CONFIG).map((entityType) => [
    entityType,
    createInitialEntityState(),
  ])
);

export const documentsSlice = createSlice({
  name: "documents",
  initialState: INITIAL_STATE,
  reducers: {
    setDocuments: (state, { payload: { entityType, documents = [] } }) => {
      state[entityType].documents = documents;
    },
    removeDocument: (state, { payload: { entityType, id } }) => {
      state[entityType].documents = state[entityType].documents.filter(
        (document) => document.id !== id
      );
    },
    setIsLoading: (state, { payload: { entityType, isLoading } }) => {
      state[entityType].isLoading = Boolean(isLoading);
    },
    setIsReadabilityView: (state, { payload: { entityType, value } }) => {
      state[entityType].isReadabilityView = value;
    },
    setReadabilityScores: (state, { payload: { entityType, scores } }) => {
      state[entityType].readabilityScores = scores;
    },
    setReadabilityThresholds: (
      state,
      { payload: { entityType, thresholds } }
    ) => {
      state[entityType].readabilityThresholds = thresholds;
    },
  },
});

export const {
  setDocuments,
  removeDocument,
  setIsLoading,
  setIsReadabilityView,
  setReadabilityScores,
  setReadabilityThresholds,
} = documentsSlice.actions;
